import { MouseEvent as ReactMouseEvent } from 'react';
import { ThreeEvent } from '@react-three/fiber';
import { Raycaster, Vector2, Vector3 } from 'three';

import { getModifierKeys } from '@paragon/utilities';

import { getActiveCamera, pixelSpaceToViewportSpace } from '../../reactThreeFiberBindings';
import { getPinchDelta, isDeltaWithinClickThreshold } from './delta';
import { ThreeJsDomEventTarget } from './ThreeJsDomEventTarget';
import { EventMetadata, EventPosition, ThreeJSDomEventType, ThreeJsDomInternalTouchEvents } from './types';
import { averagePosition, getMouseButtons, LEFT_MOUSE_BUTTON } from './util';

export class ThreeJsDom extends ThreeJsDomEventTarget {
  private domElement: HTMLDivElement | undefined;
  private readonly raycaster = new Raycaster();

  // for mouse out and on
  private shiftKey = false;
  private altKey = false;
  private ctrlKey = false;
  private deltaY: number | undefined = undefined;
  private mouseButtons = 0;
  private mouseButtonsOnLastMouseDown = 0;

  private eventPosition: EventPosition | undefined = undefined;
  private previousEventPosition: EventPosition | undefined = undefined;

  private previousPinch: Touch[] | undefined = undefined;
  private currentPinch: Touch[] | undefined = undefined;

  private mouseDownPosition: EventPosition | undefined;
  private absoluteDeltaSinceLastPointerDown: number = 0;

  private _mousedDownInScene: boolean = false;
  get mousedDownInScene() {
    return this._mousedDownInScene;
  }

  leaveScene() {
    this._mousedDownInScene = false;
  }

  public connect(domElement: HTMLDivElement) {
    if (this.domElement) throw new Error('The DOM element has already been connected.');

    this.domElement = domElement;

    // There is intentionally no provision to remove these event handlers. There can only be one `ThreeJsDom` instance,
    //   which is exported by this module and is never destroyed.
    this.domElement.addEventListener('mousemove', this.onMouseMove, false);
    this.domElement.addEventListener('mouseleave', this.onMouseLeave, false);
    this.domElement.addEventListener('mousedown', this.onMouseDown, false);
    this.domElement.addEventListener('mouseup', this.onMouseUp, false);
    this.domElement.addEventListener('contextmenu', this.onContextMenu, false);
    this.domElement.addEventListener('touchmove', this.onTouchMove, { capture: true, passive: false });
    this.domElement.addEventListener('touchstart', this.onTouchStart, false);
    this.domElement.addEventListener('touchend', this.onTouchEnd, false);
    this.domElement.addEventListener('wheel', this.onWheel, false);
  }

  public getCanvasPixelCoordsFromGlobalPixelCoords(x: number, y: number) {
    if (!this.domElement) throw new Error('The DOM element has not been connected yet.');
    const canvas = this.domElement.childNodes[0].childNodes[0].childNodes[0] as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    return { x: x - rect.left, y: y - rect.top };
  }

  private dispatchMouseMovementEvent(eventType: ThreeJSDomEventType) {
    const eventPosition = this.eventPosition!;

    const globalEvent = { eventType, eventPosition, metadata: this.getEventMetadata(eventType) };

    this.dispatchGlobalEvent(globalEvent);
  }

  private dispatchSingletonPointerEvent(eventType: ThreeJSDomEventType) {
    const eventPosition = this.eventPosition!;

    // Run a simple raycast to determine if we need to dispatch the event on any individual 3D objects
    const vector = new Vector2().set(eventPosition.viewportPosition.x, eventPosition.viewportPosition.y);
    this.raycaster.setFromCamera(vector, getActiveCamera());

    this.dispatchGlobalEvent({ eventType, eventPosition, metadata: this.getEventMetadata(eventType) });
  }

  private dispatchMouseLeaveEvent = (eventType: ThreeJSDomEventType) => {
    const eventPosition = this.eventPosition!;

    // Only the global event happens here; per-object mouse leave events are dispatched in response to mouse movement.

    this.dispatchGlobalEvent({
      eventType,
      eventPosition,
      metadata: this.getEventMetadata(eventType),
    });
  };

  getEventPosition = (source: MouseEvent | ReactMouseEvent | TouchEvent | ThreeEvent<MouseEvent>): EventPosition => {
    let event: {
      clientX: number;
      clientY: number;
      pageX: number;
      pageY: number;
    };

    if (window.TouchEvent && source instanceof TouchEvent) {
      let { touches } = source;

      // special case -- when the last touch ends, source.touches will be empty.
      if (touches.length === 0) touches = source.changedTouches;

      switch (touches.length) {
        case 0:
          throw new Error('Unable to handle touch event—no touches exist.');

        case 1:
          [event] = touches;
          break;

        default: {
          const touchList = Array.from(touches);
          event = {
            clientX: averagePosition(touchList, 'clientX'),
            clientY: averagePosition(touchList, 'clientY'),
            pageX: averagePosition(touchList, 'pageX'),
            pageY: averagePosition(touchList, 'pageY'),
          };

          break;
        }
      }
    } else {
      event = source as MouseEvent | ReactMouseEvent | ThreeEvent<MouseEvent>;
    }

    const canvasPixelPosition = this.getCanvasPixelCoordsFromGlobalPixelCoords(event.clientX, event.clientY);
    const viewportPosition = pixelSpaceToViewportSpace(canvasPixelPosition.x, canvasPixelPosition.y);
    const worldPosition = new Vector3(viewportPosition.x, viewportPosition.y, -1).unproject(getActiveCamera());

    return {
      clientPosition: { x: event.clientX, y: event.clientY },
      pagePosition: { x: event.pageX, y: event.pageY },
      canvasPixelPosition: { x: canvasPixelPosition.x, y: canvasPixelPosition.y },
      viewportPosition: { x: viewportPosition.x, y: viewportPosition.y },
      worldPosition: { x: worldPosition.x, y: worldPosition.y, z: worldPosition.z },
    };
  };

  private getEventMetadata = (type: ThreeJSDomEventType): EventMetadata => ({
    shiftKey: this.shiftKey,
    altKey: this.altKey,
    ctrlKey: this.ctrlKey,
    deltaY: this.deltaY,
    previousEventPosition: this.previousEventPosition,

    // For click events we have to use the mouse buttons from the last mouse down, since `this.mouseButtons` will
    //   already have been cleared when processing the mouse up event.
    mouseButtons: type === 'click' ? this.mouseButtonsOnLastMouseDown : this.mouseButtons,

    pinchDelta: getPinchDelta(this.previousPinch, this.currentPinch),
  });

  /**
   * This method does the universal processing that needs to happen for all event types. Must be called once for each
   *   incoming native event, before any other handling occurs, as it updates metadata that is used by everything else.
   */
  private processEventMetadata = (
    type: ThreeJSDomEventType | ThreeJsDomInternalTouchEvents,
    event: MouseEvent | TouchEvent | WheelEvent,
  ) => {
    const { shiftKey, altKey, ctrlKey } = getModifierKeys(event);
    this.shiftKey = shiftKey;
    this.altKey = altKey;
    this.ctrlKey = ctrlKey;

    const newMouseButtons = getMouseButtons(event);

    this.eventPosition = this.getEventPosition(event);

    if (this.mouseButtons && !newMouseButtons) {
      this.mouseDownPosition = undefined;
    } else if (!this.mouseButtons && newMouseButtons) {
      this.mouseDownPosition = this.eventPosition;
    }

    const isSingletonTouchEvent = window.TouchEvent && event instanceof TouchEvent && event.touches.length === 1;

    if (
      this.previousEventPosition &&
      ((type === 'mousemove' && this.mouseDownPosition) || (type === 'touchmove' && isSingletonTouchEvent))
    ) {
      this.absoluteDeltaSinceLastPointerDown =
        (this.absoluteDeltaSinceLastPointerDown ?? 0) +
        Math.sqrt(
          (this.eventPosition.clientPosition.x - this.previousEventPosition.clientPosition.x) ** 2 +
            (this.eventPosition.clientPosition.y - this.previousEventPosition.clientPosition.y) ** 2,
        );
    }

    this.mouseButtons = newMouseButtons;

    if (type === 'mousedown' || type === 'touchstart') {
      this._mousedDownInScene = true;
      this.absoluteDeltaSinceLastPointerDown = 0;
      this.mouseButtonsOnLastMouseDown = newMouseButtons;
    }

    if (type === 'mouseup' || type === 'mouseleave' || type === 'touchend') {
      this._mousedDownInScene = false;
    }

    if (window.TouchEvent && event instanceof TouchEvent && event.touches.length === 2) {
      this.previousPinch = this.currentPinch;
      this.currentPinch = Array.from(event.touches);
    } else {
      this.previousPinch = undefined;
    }

    if (event instanceof WheelEvent && event.deltaY !== undefined) {
      switch (event.deltaMode) {
        case WheelEvent.DOM_DELTA_PIXEL:
          this.deltaY = event.deltaY;
          break;

        // For the following two cases, Facebook came up with some reasonable approximations that we use because
        //   actually detecting line/page height is hard.
        // See: https://github.com/facebookarchive/fixed-data-table/blob/e45114f7aa502c5d1e2ca006b82885c0b75f32c1/src/vendor_upstream/dom/normalizeWheel.js#L21-L23

        case WheelEvent.DOM_DELTA_LINE:
          this.deltaY = 40 * event.deltaY;
          break;

        case WheelEvent.DOM_DELTA_PAGE:
          this.deltaY = 800;
          break;

        default:
          this.deltaY = undefined;
          break;
      }
    } else {
      // some track wheels set deltaY to undefined on end of wheel movement, despite typescript definition
      this.deltaY = undefined;
    }
  };

  /**
   * Like `processEventMetadata`, but should be called once per native event after all handling is done.
   */
  private processPostEventMetadata = () => {
    this.previousEventPosition = this.eventPosition;
  };

  public shouldDispatchClickEvent() {
    // Native click events are distinguished from `mouseup` because the click must begin and end on the same DOM event.
    //   Since we only have a single domElement, the native event is useless.  We can replicate that behavior somewhat
    //   by checking the delta of mouse movement from the beginning of the click to now.
    //
    // Note that this method should only be used *after* `processEvent` has been called, because it assumes that
    //   `this.mouseDownPosition` and `this.mouseButtons` have already been updated for the current event.

    // If a mouse button is still down, a click hasn't happened.
    if (this.mouseButtons || this.mouseDownPosition) return false;

    // If you ask any questions about this line I will create a task card titled "finish removing ThreeJSDom from the
    //   design client" and assign it to you. Thank you for your attention to this matter.
    //
    // And no, it's not because mobile safari sometimes fires touch events with 0 touches. We actually handle that case
    //   in `getMouseButtons` and report it as a left click.
    const clickActuallyHappened = this.mouseButtonsOnLastMouseDown !== 0;

    // Only left clicks are true clicks (context menu events are handled separately for compatibility with fiber events)
    if (clickActuallyHappened && this.mouseButtonsOnLastMouseDown !== LEFT_MOUSE_BUTTON) return false;

    return (
      this.absoluteDeltaSinceLastPointerDown === undefined ||
      isDeltaWithinClickThreshold(this.absoluteDeltaSinceLastPointerDown)
    );
  }

  public shouldDispatchContextMenuEvent() {
    return (
      this.absoluteDeltaSinceLastPointerDown === undefined ||
      isDeltaWithinClickThreshold(this.absoluteDeltaSinceLastPointerDown)
    );
  }

  /** *********************************************************************** */
  /*                    handlers for incoming native events                   */
  /** *********************************************************************** */

  private onMouseDown = (event: MouseEvent) => {
    this.processEventMetadata('mousedown', event);
    this.dispatchSingletonPointerEvent('mousedown');
    this.processPostEventMetadata();
  };

  private onMouseUp = (event: MouseEvent) => {
    this.processEventMetadata('mouseup', event);

    this.dispatchSingletonPointerEvent('mouseup');
    if (this.shouldDispatchClickEvent()) {
      this.dispatchSingletonPointerEvent('click');
    }

    this.processPostEventMetadata();
  };

  private onContextMenu = () => {
    // Context menu events must be kept isolated from our normal event processing and metadata handling because a
    // `contextmenu` event is always a duplicate of a `mouseup` or `mousedown` event (depending on platform).
    //
    // So calling the functions that process event metadata` here will lead to broken behavior. Don't do that unless you
    //   have a lot of free time to test and fix bugs. :)

    if (this.shouldDispatchContextMenuEvent()) {
      this.dispatchSingletonPointerEvent('contextmenu');
    }
  };

  private onMouseMove = (event: MouseEvent) => {
    this.processEventMetadata('mousemove', event);
    this.dispatchMouseMovementEvent('mousemove');
    this.dispatchMouseMovementEvent('mouseover');
    this.dispatchMouseMovementEvent('mouseout');
    this.processPostEventMetadata();
  };

  private onMouseLeave = (event: MouseEvent) => {
    this.processEventMetadata('mouseleave', event);
    this.dispatchMouseLeaveEvent('mouseleave');
    this.processPostEventMetadata();
  };

  private onWheel = (event: WheelEvent) => {
    this.processEventMetadata('wheel', event);
    this.dispatchSingletonPointerEvent('wheel');
    this.processPostEventMetadata();
  };

  private onTouchStart = (event: TouchEvent) => {
    this.processEventMetadata('touchstart', event);
    this.processPostEventMetadata();
  };

  private onTouchEnd = (event: TouchEvent) => {
    this.processEventMetadata('touchend', event);

    if (this.shouldDispatchClickEvent()) {
      this.dispatchSingletonPointerEvent('click');
    }

    this.processPostEventMetadata();
  };

  private onTouchMove = (event: TouchEvent) => {
    this.processEventMetadata('touchmove', event);

    const touches = event.touches.length;
    if (touches === 1) {
      this.dispatchMouseMovementEvent('mousemove');
    } else if (touches === 2) {
      this.dispatchMouseMovementEvent('touchpinch');
    }

    this.processPostEventMetadata();
  };
}

export const threeJsDom = new ThreeJsDom();
